import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ExternalLink, Mail, MapPin, Phone, User, UserPlus } from 'lucide-react';
import { AppSelectDropdown, type AppSelectOption } from '../../../components/AppSelectDropdown';
import { AddressAutocompleteInput } from '../../../components/AddressAutocompleteInput';
import {
  CONTACT_TYPES_SELECTABLE_FOR_LEAD,
  type Contact,
  type ContactType,
  type Lead,
  contactDisplayName,
  contactTypeClearsCompany,
  contactTypeRequiresCompany,
  contactTypeRequiresLastName,
  isContactSelectableForLeadInquiry,
} from '../../../types';
import { contactDetailPath, organizationDetailPath } from '../../../utils/contactRoutes';
import {
  LEAD_DETAIL_INPUT_CLASS,
  LEAD_DETAIL_LABEL_CLASS,
} from './detail/leadDetailStyles';
import { CustomerBlockNotice } from './CustomerPartySummary';

type ContactOption = {
  id: string | number;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone: string;
  address: string;
  company?: string | null;
  organization_id?: string | null;
  contact_kind?: string | null;
  is_primary?: boolean;
  is_blocked?: boolean;
  block_reason?: string;
  warning_text?: string;
};

type OrgOption = {
  id: string;
  name: string;
  is_blocked?: boolean;
  block_reason?: string;
  warning_text?: string;
};

function mapOrgOption(o: {
  id?: string;
  name?: string;
  is_blocked?: boolean;
  block_reason?: string;
  warning_text?: string;
}): OrgOption {
  return {
    id: String(o.id ?? ''),
    name: String(o.name ?? '').trim(),
    is_blocked: Boolean(o.is_blocked),
    block_reason: o.block_reason ?? '',
    warning_text: o.warning_text ?? '',
  };
}

type CustomerSelection =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'private'; contactId: string | number }
  | { kind: 'organization'; organizationId: string };

const EMPTY_FORM = {
  contact_id: null as string | number | null,
  contact_type: 'Privatkunde' as ContactType,
  first_name: '',
  last_name: '',
  company: '',
  email: '',
  phone: '',
  address: '',
};

const SELECT_TRIGGER_CLASS = '!min-h-0 h-9 !py-0 rounded-md';

export type LeadCustomerSelectionSnapshot = {
  kind: CustomerSelection['kind'];
  contactType: ContactType;
  contactId: string | number | null;
  organizationId: string | null;
  address: string;
  creatingNewCustomer: boolean;
  newPersonChosen: boolean;
};

export type LeadCustomerPickerHandle = {
  resolveContactId: (opts?: {
    addressFallback?: string | null;
  }) => Promise<{ ok: true; contactId: string } | { ok: false; error: string }>;
  getSnapshot: () => LeadCustomerSelectionSnapshot;
};

type LeadCustomerPickerProps = {
  disabled?: boolean;
  /** Prefill from an existing project (Auftraggeber switch). */
  initialLead?: Lead | null;
  showOpenLinks?: boolean;
  requiredMarks?: boolean;
  idPrefix?: string;
  labelClassName?: string;
  inputClassName?: string;
  triggerClassName?: string;
  onSelectionChange?: (snapshot: LeadCustomerSelectionSnapshot) => void;
};

function mapContactOption(c: Contact & { is_primary?: boolean }): ContactOption {
  return {
    id: c.id,
    name: c.name ?? '',
    first_name: c.first_name,
    last_name: c.last_name,
    email: c.email ?? '',
    phone: c.phone ?? '',
    address: c.address ?? '',
    company: c.company,
    organization_id: c.organization_id ?? null,
    contact_kind: c.contact_kind ?? null,
    is_primary: Boolean(c.is_primary),
    is_blocked: Boolean(c.is_blocked),
    block_reason: c.block_reason ?? '',
    warning_text: c.warning_text ?? '',
  };
}

function isLegacyCommercialContact(c: Contact): boolean {
  if (c.organization_id) return false;
  if (c.contact_kind === 'Privatkunde' || c.contact_type === 'Privatkunde') return false;
  return c.contact_type === 'Gewerblicher Kunde' || c.contact_kind === 'Gewerblicher Kontakt';
}

function applyContactToForm(prev: typeof EMPTY_FORM, c: ContactOption, companyFallback?: string | null) {
  return {
    ...prev,
    contact_id: c.id,
    first_name: (c.first_name ?? '').trim() || '',
    last_name: (c.last_name ?? '').trim() || '',
    company: (c.company ?? companyFallback ?? '').trim() || '',
    email: c.email || '',
    phone: c.phone || '',
    address: (c.address ?? '').trim() || '',
  };
}

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const err = (await res.json()) as { error?: string };
    if (err?.error) return err.error;
  } catch {
    /* ignore */
  }
  return fallback;
}

/**
 * Shared Kundenauswahl + Ansprechpartner (project sidebar and Plantafel create).
 */
export const LeadCustomerPicker = React.forwardRef<LeadCustomerPickerHandle, LeadCustomerPickerProps>(
  function LeadCustomerPicker(
    {
      initialLead = null,
      disabled,
      showOpenLinks = false,
      requiredMarks = false,
      idPrefix = 'lead-customer',
      labelClassName = LEAD_DETAIL_LABEL_CLASS,
      inputClassName = LEAD_DETAIL_INPUT_CLASS,
      triggerClassName = SELECT_TRIGGER_CLASS,
      onSelectionChange,
    },
    ref,
  ) {
    const [privateContacts, setPrivateContacts] = React.useState<ContactOption[]>([]);
    const [legacyCommercialContacts, setLegacyCommercialContacts] = React.useState<ContactOption[]>([]);
    const [organizations, setOrganizations] = React.useState<OrgOption[]>([]);
    const [orgContacts, setOrgContacts] = React.useState<ContactOption[]>([]);
    const [listsLoaded, setListsLoaded] = React.useState(false);
    const [customer, setCustomer] = React.useState<CustomerSelection>({ kind: 'none' });
    const [newPersonChosen, setNewPersonChosen] = React.useState(false);
    const [formData, setFormData] = React.useState(EMPTY_FORM);
    const [contactError, setContactError] = React.useState('');

    const onSelectionChangeRef = React.useRef(onSelectionChange);
    onSelectionChangeRef.current = onSelectionChange;
    const selectedOrgNameRef = React.useRef('');
    /** Guards against a slower organization fetch overwriting a newer selection. */
    const selectOrgRequestIdRef = React.useRef(0);
    const initializedRef = React.useRef(false);
    const formDataRef = React.useRef(formData);
    const customerRef = React.useRef(customer);
    const newPersonChosenRef = React.useRef(newPersonChosen);
    formDataRef.current = formData;
    customerRef.current = customer;
    newPersonChosenRef.current = newPersonChosen;

    const selectedOrganizationId = customer.kind === 'organization' ? customer.organizationId : null;
    const selectedOrganization =
      selectedOrganizationId != null
        ? organizations.find((o) => o.id === selectedOrganizationId) ?? null
        : null;
    selectedOrgNameRef.current = selectedOrganization?.name ?? selectedOrgNameRef.current;
    const orgMode = customer.kind === 'organization';
    const creatingNewCustomer = customer.kind === 'new';
    const editingNewContact = creatingNewCustomer || (orgMode && newPersonChosen);

    React.useEffect(() => {
      let cancelled = false;
      void Promise.all([
        fetch('/api/contacts').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/organizations?type=Gewerblicher%20Kunde').then((r) => (r.ok ? r.json() : [])),
      ])
        .then(([contactList, orgList]) => {
          if (cancelled) return;
          const rawContacts = Array.isArray(contactList) ? (contactList as Contact[]) : [];
          const selectable = rawContacts.filter((c) => {
            if (c.contact_kind === 'Privatkunde' || c.contact_kind === 'Gewerblicher Kontakt') return true;
            return isContactSelectableForLeadInquiry(c.contact_type);
          });
          setPrivateContacts(
            selectable
              .filter((c) => {
                if (c.contact_kind === 'Privatkunde') return true;
                if (c.contact_kind === 'Gewerblicher Kontakt' || c.organization_id) return false;
                return c.contact_type === 'Privatkunde';
              })
              .map((c) => mapContactOption(c)),
          );
          setLegacyCommercialContacts(
            selectable.filter((c) => isLegacyCommercialContact(c)).map((c) => mapContactOption(c)),
          );
          setOrganizations(
            (Array.isArray(orgList) ? orgList : [])
              .map((o: Parameters<typeof mapOrgOption>[0]) => mapOrgOption(o))
              .filter((o: OrgOption) => o.id && o.name),
          );
          setListsLoaded(true);
        })
        .catch(() => {
          if (cancelled) return;
          setPrivateContacts([]);
          setLegacyCommercialContacts([]);
          setOrganizations([]);
          setListsLoaded(true);
        });
      return () => {
        cancelled = true;
      };
    }, []);

    const selectOrganization = React.useCallback(
      async (
        organizationId: string,
        preferredContactId?: string | null,
        preferredFallback?: ContactOption | null,
      ) => {
        const requestId = ++selectOrgRequestIdRef.current;
        setContactError('');
        setCustomer({ kind: 'organization', organizationId });

        let orgName =
          organizations.find((o) => o.id === organizationId)?.name ?? selectedOrgNameRef.current;

        try {
          const [orgRes, contactsRes] = await Promise.all([
            orgName
              ? Promise.resolve(null)
              : fetch(`/api/organizations/${organizationId}`).then((r) => (r.ok ? r.json() : null)),
            fetch(`/api/organizations/${organizationId}/contacts`).then((r) => (r.ok ? r.json() : [])),
          ]);
          if (requestId !== selectOrgRequestIdRef.current) return;

          if (orgRes && typeof orgRes === 'object' && orgRes.name != null) {
            orgName = String(orgRes.name);
            setOrganizations((prev) => {
              const mapped = mapOrgOption({
                ...(orgRes as Parameters<typeof mapOrgOption>[0]),
                name: orgName,
              });
              return prev.some((o) => o.id === organizationId)
                ? prev.map((o) => (o.id === organizationId ? { ...o, ...mapped } : o))
                : [...prev, mapped].sort((a, b) => a.name.localeCompare(b.name, 'de'));
            });
          }
          selectedOrgNameRef.current = orgName;

          let mapped = (Array.isArray(contactsRes) ? contactsRes : []).map(
            (c: Contact & { is_primary?: boolean }) => mapContactOption(c),
          );

          const idStr =
            preferredContactId != null && preferredContactId !== '' ? String(preferredContactId) : '';
          const fromList = idStr ? mapped.find((c) => String(c.id) === idStr) : undefined;
          const fromFallback =
            !fromList && idStr && preferredFallback && String(preferredFallback.id) === idStr
              ? preferredFallback
              : undefined;
          if (fromFallback) {
            mapped = [fromFallback, ...mapped.filter((c) => String(c.id) !== idStr)];
          }
          setOrgContacts(mapped);

          if (idStr) {
            const preferred = fromList ?? fromFallback;
            if (preferred) {
              setFormData((prev) => applyContactToForm(prev, preferred, orgName));
              setNewPersonChosen(false);
            } else {
              setFormData((prev) => ({
                ...prev,
                contact_id: preferredContactId ?? prev.contact_id,
                company: orgName,
              }));
              setNewPersonChosen(false);
            }
            return;
          }

          const preferred = mapped.find((c) => c.is_primary) ?? mapped[0];
          if (preferred) {
            setFormData((prev) => applyContactToForm(prev, preferred, orgName));
            setNewPersonChosen(false);
          } else {
            setFormData((prev) => ({
              ...prev,
              contact_id: null,
              first_name: '',
              last_name: '',
              company: orgName,
              email: '',
              phone: '',
              address: '',
            }));
            setNewPersonChosen(true);
          }
        } catch {
          if (requestId !== selectOrgRequestIdRef.current) return;
          if (preferredContactId != null && preferredContactId !== '') {
            const fallback =
              preferredFallback && String(preferredFallback.id) === String(preferredContactId)
                ? preferredFallback
                : null;
            setOrgContacts(fallback ? [fallback] : []);
            if (fallback) {
              setFormData((prev) => applyContactToForm(prev, fallback, orgName));
            } else {
              setFormData((prev) => ({
                ...prev,
                contact_id: preferredContactId,
                company: orgName,
              }));
            }
            setNewPersonChosen(false);
            return;
          }
          setOrgContacts([]);
          setFormData((prev) => ({
            ...prev,
            contact_id: null,
            company: orgName,
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            address: '',
          }));
          setNewPersonChosen(true);
        }
      },
      [organizations],
    );

    const selectPrivateCustomer = React.useCallback(
      (contactId: string, fallback?: ContactOption) => {
        selectOrgRequestIdRef.current += 1;
        setContactError('');
        const c =
          privateContacts.find((x) => String(x.id) === contactId) ??
          legacyCommercialContacts.find((x) => String(x.id) === contactId) ??
          fallback;
        if (!c) return;
        setCustomer({ kind: 'private', contactId: c.id });
        setOrgContacts([]);
        setNewPersonChosen(false);
        setFormData((prev) => applyContactToForm(prev, c));
      },
      [legacyCommercialContacts, privateContacts],
    );

    const selectNewCustomer = () => {
      selectOrgRequestIdRef.current += 1;
      setContactError('');
      setCustomer({ kind: 'new' });
      setOrgContacts([]);
      setNewPersonChosen(false);
      setFormData({
        ...EMPTY_FORM,
        contact_type: 'Privatkunde',
      });
    };

    const selectOrgContact = (contactId: string) => {
      setContactError('');
      const c = orgContacts.find((x) => String(x.id) === contactId);
      if (!c) return;
      setFormData((prev) =>
        applyContactToForm(prev, c, selectedOrganization?.name ?? selectedOrgNameRef.current),
      );
      setNewPersonChosen(false);
    };

    const selectNewPerson = () => {
      setContactError('');
      setFormData((prev) => ({
        ...prev,
        contact_id: null,
        first_name: '',
        last_name: '',
        company: selectedOrganization?.name ?? selectedOrgNameRef.current,
        email: '',
        phone: '',
        address: '',
      }));
      setNewPersonChosen(true);
    };

    React.useEffect(() => {
      if (!listsLoaded || initializedRef.current || !initialLead) return;
      initializedRef.current = true;
      const orgId = initialLead?.organization?.id ?? initialLead?.contact?.organization_id ?? null;
      if (orgId) {
        void selectOrganization(
          orgId,
          initialLead?.contact?.id != null ? String(initialLead.contact.id) : undefined,
          initialLead?.contact ? mapContactOption(initialLead.contact) : undefined,
        );
        return;
      }
      if (initialLead?.contact?.id != null) {
        selectPrivateCustomer(String(initialLead.contact.id), mapContactOption(initialLead.contact));
      }
    }, [initialLead, listsLoaded, selectOrganization, selectPrivateCustomer]);

    const customerValue =
      customer.kind === 'new'
        ? '__new__'
        : customer.kind === 'organization'
          ? `org:${customer.organizationId}`
          : customer.kind === 'private'
            ? `contact:${customer.contactId}`
            : '';

    const knownPrivateIds = new Set([
      ...privateContacts.map((c) => String(c.id)),
      ...legacyCommercialContacts.map((c) => String(c.id)),
    ]);
    const missingCurrentPrivate =
      customer.kind === 'private' && !knownPrivateIds.has(String(customer.contactId))
        ? {
            value: `contact:${customer.contactId}`,
            label:
              `${contactDisplayName(formData) || formData.company || 'Kontakt'}${
                formData.email ? ` · ${formData.email}` : ''
              }`.trim(),
            group: formData.company ? 'Gewerbliche Kunden' : 'Privatkunden',
            icon: formData.company ? <Building2 className="h-3.5 w-3.5" /> : undefined,
          }
        : null;

    const customerOptions: AppSelectOption[] = [
      ...(creatingNewCustomer
        ? [{ value: '__new__', label: 'Neuen Kunden erstellen', icon: <UserPlus className="h-4 w-4" /> }]
        : []),
      ...organizations.map((o) => ({
        value: `org:${o.id}`,
        label: o.name,
        group: 'Gewerbliche Kunden',
        icon: <Building2 className="h-3.5 w-3.5" />,
      })),
      ...legacyCommercialContacts.map((c) => ({
        value: `contact:${c.id}`,
        label: `${(c.company || '').trim() || contactDisplayName(c)}${c.email ? ` · ${c.email}` : ''}`.trim(),
        group: 'Gewerbliche Kunden',
        icon: <Building2 className="h-3.5 w-3.5" />,
      })),
      ...(missingCurrentPrivate && missingCurrentPrivate.group === 'Gewerbliche Kunden'
        ? [missingCurrentPrivate]
        : []),
      ...privateContacts.map((c) => ({
        value: `contact:${c.id}`,
        label: `${contactDisplayName(c)}${c.email ? ` · ${c.email}` : ''}`.trim(),
        group: 'Privatkunden',
      })),
      ...(missingCurrentPrivate && missingCurrentPrivate.group === 'Privatkunden'
        ? [missingCurrentPrivate]
        : []),
    ];

    const personValue = newPersonChosen
      ? '__new_person__'
      : formData.contact_id != null
        ? String(formData.contact_id)
        : '';

    const knownPersonIds = new Set(orgContacts.map((c) => String(c.id)));
    const missingCurrentPerson =
      orgMode &&
      !newPersonChosen &&
      formData.contact_id != null &&
      !knownPersonIds.has(String(formData.contact_id))
        ? {
            value: String(formData.contact_id),
            label:
              `${contactDisplayName(formData) || formData.company || 'Kontakt'}${
                formData.email ? ` · ${formData.email}` : ''
              }`.trim(),
          }
        : null;

    const personOptions: AppSelectOption[] = [
      ...(newPersonChosen
        ? [
            {
              value: '__new_person__',
              label: 'Weiteren Ansprechpartner hinzufügen',
              icon: <UserPlus className="h-4 w-4" />,
            },
          ]
        : []),
      ...(missingCurrentPerson ? [missingCurrentPerson] : []),
      ...orgContacts.map((c) => ({
        value: String(c.id),
        label: `${contactDisplayName(c)}${c.email ? ` · ${c.email}` : ''}${c.is_primary ? ' · Haupt' : ''}`.trim(),
      })),
    ];

    const selectedContactId = editingNewContact
      ? null
      : customer.kind === 'private'
        ? customer.contactId
        : formData.contact_id;
    const selectedOrgIdForLink = orgMode ? selectedOrganizationId : null;

    const getSnapshot = React.useCallback((): LeadCustomerSelectionSnapshot => {
      const current = customerRef.current;
      const data = formDataRef.current;
      return {
        kind: current.kind,
        contactType: data.contact_type,
        contactId:
          current.kind === 'private'
            ? current.contactId
            : newPersonChosenRef.current
              ? null
              : data.contact_id,
        organizationId: current.kind === 'organization' ? current.organizationId : null,
        address: data.address,
        creatingNewCustomer: current.kind === 'new',
        newPersonChosen: newPersonChosenRef.current,
      };
    }, []);

    React.useEffect(() => {
      onSelectionChangeRef.current?.(getSnapshot());
    }, [
      getSnapshot,
      customer,
      formData.contact_id,
      formData.contact_type,
      formData.address,
      newPersonChosen,
    ]);

    React.useImperativeHandle(ref, () => ({
      getSnapshot,
      resolveContactId: async (opts) => {
        const data = formDataRef.current;
        const currentCustomer = customerRef.current;
        const isOrg = currentCustomer.kind === 'organization';
        const isNewCustomer = currentCustomer.kind === 'new';
        const isNewPerson = newPersonChosenRef.current;
        const orgId = isOrg ? currentCustomer.organizationId : null;
        const orgName = selectedOrgNameRef.current;
        const address =
          data.address.trim() || (opts?.addressFallback != null ? String(opts.addressFallback).trim() : '') || null;

        setContactError('');

        if (currentCustomer.kind === 'none') {
          const message = 'Bitte wählen Sie einen Kunden oder „Neuen Kunden erstellen“.';
          setContactError(message);
          return { ok: false as const, error: message };
        }
        if (isOrg && !isNewPerson && data.contact_id == null) {
          const message = 'Bitte wählen Sie einen Ansprechpartner oder fügen Sie einen weiteren hinzu.';
          setContactError(message);
          return { ok: false as const, error: message };
        }
        if (isOrg && isNewPerson && !data.first_name.trim() && !data.last_name.trim()) {
          const message = 'Bitte Vor- und/oder Nachname des Ansprechpartners angeben.';
          setContactError(message);
          return { ok: false as const, error: message };
        }
        if (isNewCustomer && contactTypeRequiresLastName(data.contact_type) && !data.last_name.trim()) {
          const message = 'Bitte den Nachnamen angeben.';
          setContactError(message);
          return { ok: false as const, error: message };
        }
        if (isNewCustomer && contactTypeRequiresCompany(data.contact_type) && !data.company.trim()) {
          const message = 'Bitte den Unternehmensnamen angeben.';
          setContactError(message);
          return { ok: false as const, error: message };
        }

        try {
          if (isOrg && isNewPerson && orgId) {
            const contactRes = await fetch('/api/contacts', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                first_name: data.first_name.trim() || null,
                last_name: data.last_name.trim() || null,
                email: data.email.trim() || null,
                phone: data.phone.trim() || null,
                address,
                contact_kind: 'Gewerblicher Kontakt',
                organization_id: orgId,
              }),
            });
            if (!contactRes.ok) {
              const message = await readErrorMessage(
                contactRes,
                'Ansprechpartner konnte nicht angelegt werden.',
              );
              setContactError(message);
              return { ok: false as const, error: message };
            }
            const created = (await contactRes.json()) as { id?: string | number };
            if (created.id == null) {
              const message = 'Ansprechpartner konnte nicht angelegt werden.';
              setContactError(message);
              return { ok: false as const, error: message };
            }
            const createdOption: ContactOption = {
              id: created.id,
              name: '',
              first_name: data.first_name.trim() || null,
              last_name: data.last_name.trim() || null,
              email: data.email.trim(),
              phone: data.phone.trim(),
              address: address ?? '',
              company: orgName || data.company,
              organization_id: orgId,
              contact_kind: 'Gewerblicher Kontakt',
            };
            const nextForm = applyContactToForm(data, createdOption, orgName);
            formDataRef.current = nextForm;
            setFormData(nextForm);
            newPersonChosenRef.current = false;
            setNewPersonChosen(false);
            setOrgContacts((prev) =>
              prev.some((c) => String(c.id) === String(created.id)) ? prev : [...prev, createdOption],
            );
            return { ok: true as const, contactId: String(created.id) };
          }

          if (isNewCustomer) {
            const isPrivate = data.contact_type === 'Privatkunde';
            const contactRes = await fetch('/api/contacts', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                first_name: data.first_name.trim() || null,
                last_name: data.last_name.trim() || null,
                email: data.email.trim() || null,
                phone: data.phone.trim() || null,
                address,
                company: isPrivate ? null : data.company.trim() || orgName || null,
                contact_kind: isPrivate ? 'Privatkunde' : 'Gewerblicher Kontakt',
                contact_type: data.contact_type,
              }),
            });
            if (!contactRes.ok) {
              const message = await readErrorMessage(contactRes, 'Kontakt konnte nicht angelegt werden.');
              setContactError(message);
              return { ok: false as const, error: message };
            }
            const created = (await contactRes.json()) as { id?: string | number };
            if (created.id == null) {
              const message = 'Kontakt konnte nicht angelegt werden.';
              setContactError(message);
              return { ok: false as const, error: message };
            }
            const createdOption: ContactOption = {
              id: created.id,
              name: '',
              first_name: data.first_name.trim() || null,
              last_name: data.last_name.trim() || null,
              email: data.email.trim(),
              phone: data.phone.trim(),
              address: address ?? '',
              company: isPrivate ? null : data.company.trim() || orgName || null,
              organization_id: null,
              contact_kind: isPrivate ? 'Privatkunde' : 'Gewerblicher Kontakt',
            };
            const nextForm = applyContactToForm(data, createdOption);
            formDataRef.current = nextForm;
            setFormData(nextForm);
            const nextCustomer: CustomerSelection = { kind: 'private', contactId: created.id };
            customerRef.current = nextCustomer;
            setCustomer(nextCustomer);
            newPersonChosenRef.current = false;
            setNewPersonChosen(false);
            if (isPrivate) {
              setPrivateContacts((prev) =>
                prev.some((c) => String(c.id) === String(created.id)) ? prev : [...prev, createdOption],
              );
            } else {
              setLegacyCommercialContacts((prev) =>
                prev.some((c) => String(c.id) === String(created.id)) ? prev : [...prev, createdOption],
              );
            }
            return { ok: true as const, contactId: String(created.id) };
          }

          if (data.contact_id == null) {
            const message = 'Bitte wählen Sie einen Kunden.';
            setContactError(message);
            return { ok: false as const, error: message };
          }
          return { ok: true as const, contactId: String(data.contact_id) };
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Kontakt konnte nicht gespeichert werden.';
          setContactError(message);
          return { ok: false as const, error: message };
        }
      },
    }));

    const openLinkClass =
      'inline-flex items-center gap-1 text-[12px] font-medium text-pipedrive-blue hover:underline';

    const requiredMark = requiredMarks ? <span className="text-red-500"> *</span> : null;

    return (
      <div className="space-y-3">
        <div>
          <label className={labelClassName} htmlFor={`${idPrefix}-customer-select`}>
            Kundenauswahl
            {requiredMark}
          </label>
          <AppSelectDropdown
            id={`${idPrefix}-customer-select`}
            value={customerValue}
            disabled={disabled}
            searchable
            searchPlaceholder="Suchen..."
            placeholder="Kunde auswählen"
            onCreate={selectNewCustomer}
            createLabel="Neuen Kunden erstellen"
            createIcon={<UserPlus className="h-4 w-4" />}
            options={customerOptions}
            triggerClassName={`${triggerClassName}${contactError && customer.kind === 'none' ? ' border-red-400' : ''}`}
            onChange={(v) => {
              if (v === '__new__') {
                selectNewCustomer();
                return;
              }
              if (v.startsWith('org:')) {
                void selectOrganization(v.slice(4));
                return;
              }
              if (v.startsWith('contact:')) {
                selectPrivateCustomer(v.slice(8));
              }
            }}
          />
        </div>

        {(() => {
          const selected =
            customer.kind === 'organization'
              ? organizations.find((o) => o.id === customer.organizationId)
              : customer.kind === 'private'
                ? privateContacts.find((c) => String(c.id) === String(customer.contactId)) ??
                  legacyCommercialContacts.find((c) => String(c.id) === String(customer.contactId))
                : null;
          if (!selected) return null;
          return (
            <CustomerBlockNotice
              isBlocked={selected.is_blocked}
              blockReason={selected.block_reason}
              warningText={selected.warning_text}
            />
          );
        })()}

        {orgMode ? (
          <div>
            <label className={labelClassName} htmlFor={`${idPrefix}-person-select`}>
              Ansprechpartner
              {requiredMark}
            </label>
            <AppSelectDropdown
              id={`${idPrefix}-person-select`}
              value={personValue}
              disabled={disabled}
              searchable
              searchPlaceholder="Suchen..."
              placeholder="Ansprechpartner auswählen"
              onCreate={selectNewPerson}
              createLabel="Weiteren Ansprechpartner hinzufügen"
              createIcon={<UserPlus className="h-4 w-4" />}
              options={personOptions}
              triggerClassName={`${triggerClassName}${contactError && orgMode ? ' border-red-400' : ''}`}
              onChange={(v) => {
                if (v === '__new_person__') {
                  selectNewPerson();
                  return;
                }
                selectOrgContact(v);
              }}
            />
          </div>
        ) : null}

        {creatingNewCustomer ? (
          <div>
            <span className={labelClassName}>Kontaktart{requiredMark}</span>
            <div
              className="flex w-full flex-wrap gap-0.5 rounded-lg border border-app-border bg-app-surface-muted/80 p-0.5"
              role="group"
              aria-label="Kontaktart"
            >
              {CONTACT_TYPES_SELECTABLE_FOR_LEAD.map((t) => (
                <button
                  key={t}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    setContactError('');
                    setFormData((prev) => ({
                      ...prev,
                      contact_type: t,
                      company: contactTypeClearsCompany(t) ? '' : prev.company,
                    }));
                  }}
                  className={`min-w-0 flex-1 rounded-md px-2 py-1.5 text-[11px] font-medium leading-tight transition-all disabled:opacity-60 ${
                    formData.contact_type === t
                      ? 'bg-app-surface text-app-text shadow-sm ring-1 ring-slate-200/80'
                      : 'text-app-text-muted hover:bg-app-hover hover:text-app-text'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {orgMode && newPersonChosen ? (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClassName} htmlFor={`${idPrefix}-new-person-first-name`}>
                Vorname
              </label>
              <div className="relative">
                <User className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  id={`${idPrefix}-new-person-first-name`}
                  type="text"
                  value={formData.first_name}
                  disabled={disabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                  className={`${inputClassName} pl-8`}
                  placeholder="Max"
                />
              </div>
            </div>
            <div>
              <label className={labelClassName} htmlFor={`${idPrefix}-new-person-last-name`}>
                Nachname
              </label>
              <div className="relative">
                <User className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  id={`${idPrefix}-new-person-last-name`}
                  type="text"
                  value={formData.last_name}
                  disabled={disabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                  className={`${inputClassName} pl-8`}
                  placeholder="Mustermann"
                />
              </div>
            </div>
          </div>
        ) : null}

        {creatingNewCustomer && formData.contact_type === 'Privatkunde' ? (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClassName} htmlFor={`${idPrefix}-new-private-first-name`}>
                Vorname
              </label>
              <div className="relative">
                <User className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  id={`${idPrefix}-new-private-first-name`}
                  type="text"
                  value={formData.first_name}
                  disabled={disabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                  className={`${inputClassName} pl-8`}
                  placeholder="Max"
                />
              </div>
            </div>
            <div>
              <label className={labelClassName} htmlFor={`${idPrefix}-new-private-last-name`}>
                Nachname <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  id={`${idPrefix}-new-private-last-name`}
                  type="text"
                  value={formData.last_name}
                  disabled={disabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                  className={`${inputClassName} pl-8`}
                  placeholder="Mustermann"
                />
              </div>
            </div>
          </div>
        ) : null}

        {creatingNewCustomer && formData.contact_type === 'Gewerblicher Kunde' ? (
          <div className="space-y-3">
            <div className="space-y-2 rounded-lg border border-app-border bg-app-surface-muted/70 p-2.5">
              <span className={labelClassName}>Unternehmen</span>
              <div>
                <label className={labelClassName} htmlFor={`${idPrefix}-new-company-name`}>
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id={`${idPrefix}-new-company-name`}
                  type="text"
                  value={formData.company}
                  disabled={disabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                  className={inputClassName}
                  placeholder="Muster GmbH"
                />
              </div>
              <div>
                <label className={labelClassName} htmlFor={`${idPrefix}-new-company-address`}>
                  Firmensitz
                </label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-2.5 top-[11px] z-[1] h-3.5 w-3.5 text-slate-400" />
                  <AddressAutocompleteInput
                    id={`${idPrefix}-new-company-address`}
                    value={formData.address}
                    onChange={(v) => setFormData((prev) => ({ ...prev, address: v }))}
                    placeholder="Straße, PLZ Ort"
                    inputClassName={`${inputClassName} pl-8`}
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2 rounded-lg border border-app-border bg-app-surface p-2.5">
              <span className={labelClassName}>Ansprechpartner</span>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClassName} htmlFor={`${idPrefix}-new-org-first-name`}>
                    Vorname
                  </label>
                  <input
                    id={`${idPrefix}-new-org-first-name`}
                    type="text"
                    value={formData.first_name}
                    disabled={disabled}
                    onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                    className={inputClassName}
                    placeholder="Max"
                  />
                </div>
                <div>
                  <label className={labelClassName} htmlFor={`${idPrefix}-new-org-last-name`}>
                    Nachname
                  </label>
                  <input
                    id={`${idPrefix}-new-org-last-name`}
                    type="text"
                    value={formData.last_name}
                    disabled={disabled}
                    onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                    className={inputClassName}
                    placeholder="Mustermann"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {editingNewContact ? (
          <div className="space-y-2">
            <div>
              <label className={labelClassName} htmlFor={`${idPrefix}-new-email`}>
                E-Mail
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  id={`${idPrefix}-new-email`}
                  type="email"
                  value={formData.email}
                  disabled={disabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className={`${inputClassName} pl-8`}
                  placeholder="kunde@beispiel.de"
                />
              </div>
            </div>
            <div>
              <label className={labelClassName} htmlFor={`${idPrefix}-new-phone`}>
                Telefon
              </label>
              <div className="relative">
                <Phone className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  id={`${idPrefix}-new-phone`}
                  type="tel"
                  value={formData.phone}
                  disabled={disabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  className={`${inputClassName} pl-8`}
                  placeholder="+49 …"
                />
              </div>
            </div>
          </div>
        ) : null}

        {creatingNewCustomer && formData.contact_type === 'Privatkunde' ? (
          <div>
            <label className={labelClassName} htmlFor={`${idPrefix}-new-private-address`}>
              Adresse
            </label>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-2.5 top-[11px] z-[1] h-3.5 w-3.5 text-slate-400" />
              <AddressAutocompleteInput
                id={`${idPrefix}-new-private-address`}
                value={formData.address}
                onChange={(v) => setFormData((prev) => ({ ...prev, address: v }))}
                placeholder="Straße, PLZ Ort"
                inputClassName={`${inputClassName} pl-8`}
                disabled={disabled}
              />
            </div>
          </div>
        ) : null}

        {contactError ? <p className="text-[11px] leading-snug text-red-600">{contactError}</p> : null}

        {showOpenLinks && (selectedContactId != null || selectedOrgIdForLink) ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {selectedContactId != null ? (
              <Link to={contactDetailPath(selectedContactId)} className={openLinkClass}>
                Kontakt öffnen & bearbeiten
                <ExternalLink className="h-3 w-3" aria-hidden />
              </Link>
            ) : null}
            {selectedOrgIdForLink ? (
              <Link to={organizationDetailPath(selectedOrgIdForLink)} className={openLinkClass}>
                Organisation öffnen
                <ExternalLink className="h-3 w-3" aria-hidden />
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);
